import { useTranslation } from 'react-i18next';
import { ImageOff } from 'lucide-react';
import BoardGridDisplay from '@/components/climbs/board-grid/BoardGridDisplay';
import { isShapedWall, scannedWallOf } from '@/components/climbs/board-grid/geometry';
import { Card, CardContent } from '@/components/ui/card';
import type { Board } from '../../../types/device';
import type { BoardDashboardClimb } from '../../../types/board-dashboard';

interface BoardPhotoPanelProps {
  board: Board;
  /** The problem drawn on the photo; null leaves the bare wall. */
  selected: BoardDashboardClimb | null;
}

/**
 * The left half of the dashboard: the board photo with the selected problem's
 * holds drawn on it. Everything else about the problem — its numbers, its
 * angles, its history — is read off its row in the list, which opens up when
 * it is picked.
 */
export function BoardPhotoPanel({ board, selected }: BoardPhotoPanelProps) {
  const { t } = useTranslation();
  // A wall described by its photo's scan: its holds are drawn as traced.
  const scanned = scannedWallOf(board.scanned_holds, board.image_crop, board.hidden_scanned_holds);

  return (
    <div className="space-y-3">
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {/* A wall drawn as lines has no grid to need: its holds are the ones
              on the lines, so rows and columns say nothing about whether there
              is anything to draw. */}
          {board.image_url && (scanned || isShapedWall(board.shapes) || (board.rows && board.columns)) ? (
            <BoardGridDisplay
              imageUrl={board.image_url}
              rows={board.rows ?? 0}
              columns={board.columns ?? 0}
              calibration={board.calibration}
              holds={selected?.holds ?? []}
              boardHolds={board.holds}
              extraHolds={board.extra_holds}
              shapes={board.shapes}
              scanned={scanned}
              holdRadii={board.hold_radii}
              circuit={selected?.circuit ?? false}
              // Reading a problem off a dense wall means looking closely at
              // which holds are in it. Wheel and pinch, as on the form that
              // sets it, with only the way back out in the corner.
              zoomable
              zoomControls="reset"
            />
          ) : (
            <div className="flex aspect-[3/4] flex-col items-center justify-center gap-2 bg-muted text-muted-foreground">
              <ImageOff className="size-8" aria-hidden />
              <p className="px-6 text-center text-sm">
                {t('gymAdmin.boardDashboard.noBoardImage')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {selected === null && (
        <p className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
          {t('gymAdmin.boardDashboard.pickAProblem')}
        </p>
      )}
    </div>
  );
}
